import { useMemo } from 'react'
import useParams from './hooks/useParams'
import useToken from './hooks/useToken'
import usePreferences from './hooks/usePreferences'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Paper from '@mui/material/Paper'
import { ThemeProvider } from '@mui/material/styles'
import HotglueConnections from './components/HotglueConnections'
import CompanyBanner from './components/CompanyBanner'
import { CompanyBannerCompact } from './components/CompanyBannerCompact'
import { BannerActions } from './components/BannerActions'
import theme from './theme'

type Layout = 'default' | 'compact'

const pageRootSx = {
  position: 'relative',
  backgroundColor: '#eeeeee',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    pointerEvents: 'none',
  },
} as const

const compactConnectorComponentStyle = {
  root: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    borderBottom: '1px solid #e3e3e6',
    paddingBottom: 16,
  },
  nameIconContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  icon: {
    marginBottom: 0,
    width: 32,
    height: 32,
    border: 'none',
  },
  name: {
    marginBottom: 0,
  },
} as const

export default function App() {
  const token = useParams('token')
  const { data, error, isLoading } = useToken(token)
  const { data: preferences } = usePreferences(data?.envId ?? '', data?.apiKey ?? '')
  const layout: Layout = preferences?.connectLayout ?? 'default'

  const customTheme = useMemo(
    () => ({
      ...theme,
      palette: {
        ...theme.palette,
        primary: {
          ...theme.palette.primary,
          main: preferences?.primaryColor ?? '#fa5442',
        },
      },
    }),
    [preferences?.primaryColor]
  )

  const missingToken = !token
  const fetchFailedWithoutError = !isLoading && !data && !error
  const showMagicLinkError = missingToken || (!isLoading && !data)
  const magicLinkError =
    error ??
    (missingToken
      ? 'This link is invalid.'
      : fetchFailedWithoutError
        ? 'Something went wrong, please try again.'
        : undefined)
  const baseBannerProps = {
    isLoading,
    error: magicLinkError,
    envId: data?.envId,
    apiKey: data?.apiKey,
  }
  const bannerProps = { ...baseBannerProps, hasError: showMagicLinkError }
  const connectorComponentStyle =
    layout === 'compact' ? compactConnectorComponentStyle : undefined

  return (
    <ThemeProvider theme={customTheme}>
      <Box className="connect-page-root" display="flex" height="100vh" sx={pageRootSx}>
        {showMagicLinkError ? (
          <Container
            maxWidth="sm"
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <Paper
              elevation={2}
              sx={{
                width: '100%',
                p: 4,
                borderRadius: 4,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 18px 16px 0 rgba(201, 201, 201, 0.25)',
              }}
            >
              <CompanyBannerCompact {...bannerProps} />
            </Paper>
          </Container>
        ) : layout === 'default' ? (
          <Container maxWidth="xl" sx={{ position: 'relative', display: 'flex', width: '100%', py: 4 }}>
            <Box display="flex" gap={5} width="100%" minHeight={0}>
              <Box
                flex="0 0 44%"
                maxWidth={480}
                minWidth={280}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <CompanyBanner {...baseBannerProps} />
              </Box>

              {data && !isLoading && (
                <Box flex={1} minWidth={0} minHeight={0} overflow="auto" display="flex" flexDirection="column">
                  <HotglueConnections
                    data={data}
                    connectorComponentStyle={connectorComponentStyle}
                  />
                </Box>
              )}
            </Box>
          </Container>
        ) : (
          <Container
            maxWidth="sm"
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
              gap: 3,
            }}
          >
            <Paper
              elevation={2}
              sx={{
                width: '100%',
                p: 4,
                borderRadius: 4,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                boxShadow: '0 18px 16px 0 rgba(201, 201, 201, 0.25)',
                maxHeight: '600px',
              }}
            >
              <CompanyBannerCompact {...bannerProps} />
              {data && !isLoading && (
                <HotglueConnections
                  data={data}
                  connectorComponentStyle={connectorComponentStyle}
                />
              )}
            </Paper>

            <BannerActions envId={data?.envId} apiKey={data?.apiKey} isLoading={isLoading} layout={layout} />
          </Container>
        )}
      </Box>
    </ThemeProvider>
  )
}
